import { Button } from "components/ui/Button/Button";
import { Loading } from "components/ui/Loading/Loading";
import { Select } from "components/ui/Select/Select";
import { Textarea } from "components/ui/Textarea/Textarea";
import { type PromptType, useEmailCompose } from "hooks/use-email-compose";
import { useComposeSenders } from "hooks/use-email-integrations-api";
import type { ReplyEmailItem } from "hooks/use-email-replies-api";
import { useScrollToBottomWhile } from "hooks/use-scroll-to-bottom";
import { type FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AiComposeSection } from "routes/emails/EmailDetailPage/components/AiComposeSection";
import { toastError } from "utils/toast-utils";

import { buildReplySeed } from "../lib/build-reply-seed";

/** 返信管理のインライン返信で選べるプロンプト種別 (提案系は元メール文脈が要るため除外) */
const INLINE_PROMPT_TYPES: PromptType[] = [
  "custom",
  "inquiry",
  "negotiate",
  "schedule",
  "decline",
  "followup",
];

export type InlineReplyComposerProps = {
  /** 選択中の reply (返信先) */
  reply: ReplyEmailItem;
  /** Gmail 連携が有効か。false なら送信不可 */
  canCompose: boolean;
  /** 送信成功時のコールバック (一覧 refetch 等) */
  onSent: () => void;
};

/**
 * 返信管理画面の返信フォーム列に埋め込むフル機能の返信フォーム.
 *
 * 送信元選択・宛先(To)・CC/BCC・件名・AI 生成・本文・送信を 1 箇所で完結させる
 * (フル画面モーダルへ切り替えずに編集できる). AI 生成の操作は Gmail 風の
 * 「自由入力プロンプト」 1 つで、ユーザは短い指示を書き「生成」 で本文を生成する.
 * プロンプトは promptType="custom" として送られ、同スレッド全体を文脈に生成される.
 *
 * @param {InlineReplyComposerProps} props 選択 reply とハンドラ
 * @return {JSX.Element} 返信フォーム
 */
export const InlineReplyComposer: FC<InlineReplyComposerProps> = ({
  reply,
  canCompose,
  onSent,
}) => {
  const {
    draft,
    isGenerating,
    isSending,
    error,
    startReplyFromSeed,
    setField,
    addRecipient,
    removeRecipient,
    generateBody,
    sendEmail,
    cancelGeneration,
  } = useEmailCompose();

  const { senders } = useComposeSenders();

  const [showCcBcc, setShowCcBcc] = useState(false);
  const [newCc, setNewCc] = useState("");
  const [newBcc, setNewBcc] = useState("");

  // 生成中は本文を末尾へ自動スクロールし、ストリーミング表示を常に見えるようにする
  const bodyRef = useScrollToBottomWhile(isGenerating, draft?.body);

  // **Identity ベース**で再シード. reply オブジェクトは refetch のたびに新参照になり
  // 中身が同じでも再生成されるため、 reply.id を deps にして
  // ユーザがすでに入力した本文・プロンプトを保護する。
  // ref(参照)を依存に入れると送信→refetch のたびに draft / promptText が wipe される.
  // biome-ignore lint/correctness/useExhaustiveDependencies: identity-only re-seed by design
  useEffect(() => {
    startReplyFromSeed(buildReplySeed(reply));
    setShowCcBcc(false);
    setNewCc("");
    setNewBcc("");
  }, [reply.id, startReplyFromSeed]);

  /**
   * 送信を実行する
   * @return {Promise<void>}
   */
  const handleSend = async (): Promise<void> => {
    const success = await sendEmail();
    if (success) {
      toast.success("返信メールを送信しました");
      onSent();
    } else {
      toastError("メールの送信に失敗しました");
    }
  };

  /**
   * CC / BCC アドレスを追加する
   * @param {"cc" | "bcc"} type 宛先種別
   * @return {void}
   */
  const handleAddCcBcc = (type: "cc" | "bcc"): void => {
    const value = type === "cc" ? newCc : newBcc;
    const trimmed = value.trim();
    if (!trimmed) return;
    addRecipient({ email: trimmed, name: "", type });
    if (type === "cc") setNewCc("");
    else setNewBcc("");
  };

  if (!draft) {
    return (
      <div className="flex justify-center py-3">
        <Loading size="xs" />
      </div>
    );
  }

  const toRecipients = draft.recipients.filter(r => r.type === "to");
  const ccRecipients = draft.recipients.filter(r => r.type === "cc");
  const bccRecipients = draft.recipients.filter(r => r.type === "bcc");
  const hasSender = senders.length > 0 || draft.fromEmail.length > 0;

  const canSend =
    canCompose && !isGenerating && !isSending && draft.body.trim().length > 0;

  return (
    <div className="space-y-2">
      {/* 送信元 */}
      <div className="space-y-0.5">
        <span className="text-xs text-base-content/60">送信元</span>
        {hasSender ? (
          <Select
            size="sm"
            options={
              senders.length === 0 && draft.fromEmail
                ? [
                    {
                      value: draft.fromEmail,
                      label: `${draft.fromName} (${draft.fromEmail})`,
                    },
                  ]
                : senders.map(s => ({
                    value: s.email,
                    label: `${s.displayName} (${s.email})`,
                  }))
            }
            value={draft.fromEmail}
            onChange={next => {
              if (!next) return;
              setField("fromEmail", next);
              const sender = senders.find(s => s.email === next);
              if (sender) setField("fromName", sender.displayName);
            }}
          />
        ) : (
          <div className="text-xs text-rose-600">
            送信には Gmail 連携が必要です
          </div>
        )}
      </div>

      {/* 宛先 (To) */}
      <div className="space-y-0.5">
        <span className="text-xs text-base-content/60">宛先 (To)</span>
        <div className="space-y-1">
          {toRecipients.map(r => (
            <div
              key={r.email}
              className="input input-bordered input-sm w-full !h-auto py-1 bg-gray-50 text-sm text-gray-600 leading-snug break-all"
            >
              {r.name ? `${r.name} <${r.email}>` : r.email}
            </div>
          ))}
        </div>
        {!showCcBcc && (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() => setShowCcBcc(true)}
          >
            + CC/BCC を追加
          </Button>
        )}
      </div>

      {/* CC / BCC */}
      {showCcBcc && (
        <>
          <div className="space-y-0.5">
            <span className="text-xs text-base-content/60">CC</span>
            <div className="space-y-1">
              {ccRecipients.map(r => (
                <div key={r.email} className="flex items-center gap-1">
                  <span className="input input-bordered input-sm flex-1 min-w-0 !h-auto py-1 bg-gray-50 text-sm text-gray-600 leading-snug break-all">
                    {r.email}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    shape="square"
                    onClick={() => removeRecipient(r.email, "cc")}
                    aria-label="CC を削除"
                  >
                    ✕
                  </Button>
                </div>
              ))}
              <div className="flex gap-1">
                <input
                  type="email"
                  className="input input-bordered input-sm flex-1"
                  placeholder="cc@example.com"
                  value={newCc}
                  onChange={e => setNewCc(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCcBcc("cc");
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  shape="square"
                  onClick={() => handleAddCcBcc("cc")}
                  aria-label="CC を追加"
                >
                  +
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-0.5">
            <span className="text-xs text-base-content/60">BCC</span>
            <div className="space-y-1">
              {bccRecipients.map(r => (
                <div key={r.email} className="flex items-center gap-1">
                  <span className="input input-bordered input-sm flex-1 min-w-0 !h-auto py-1 bg-gray-50 text-sm text-gray-600 leading-snug break-all">
                    {r.email}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    shape="square"
                    onClick={() => removeRecipient(r.email, "bcc")}
                    aria-label="BCC を削除"
                  >
                    ✕
                  </Button>
                </div>
              ))}
              <div className="flex gap-1">
                <input
                  type="email"
                  className="input input-bordered input-sm flex-1"
                  placeholder="bcc@example.com"
                  value={newBcc}
                  onChange={e => setNewBcc(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCcBcc("bcc");
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  shape="square"
                  onClick={() => handleAddCcBcc("bcc")}
                  aria-label="BCC を追加"
                >
                  +
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 件名 */}
      <div className="space-y-0.5">
        <span className="text-xs text-base-content/60">件名</span>
        <input
          type="text"
          className="input input-bordered input-sm w-full"
          value={draft.subject}
          onChange={e => setField("subject", e.target.value)}
        />
      </div>

      {/* AI 生成セクション (共通部品) */}
      <AiComposeSection
        promptTypes={INLINE_PROMPT_TYPES}
        defaultPromptType="custom"
        isGenerating={isGenerating}
        margin={draft.margin}
        onMarginChange={value => setField("margin", value)}
        proposalInfo={draft.proposalInfo}
        onProposalInfoChange={value => setField("proposalInfo", value)}
        onGenerate={generateBody}
        onCancel={cancelGeneration}
        hint="スレッド全体を読んだ上で生成します"
      />

      {/* 本文 */}
      <Textarea
        ref={bodyRef}
        size="sm"
        rows={8}
        value={draft.body}
        onChange={e => setField("body", e.target.value)}
        placeholder={
          isGenerating
            ? "AI が本文を生成しています..."
            : "返信内容 (生成結果はここに反映 / 直接編集も可)"
        }
      />

      {error && (
        <div className="text-xs text-rose-600 break-words">{error}</div>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          onClick={handleSend}
          disabled={!canSend}
          title={canCompose ? undefined : "返信送信には Gmail 連携が必要です"}
        >
          {isSending ? (
            <span className="inline-flex items-center gap-1">
              <Loading size="xs" />
              送信中
            </span>
          ) : (
            "送信"
          )}
        </Button>
      </div>
    </div>
  );
};
